import { FC, RefObject } from "react";

import { Box, Stack } from "@mui/material";
import { Ratio } from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { FormStepContainer } from "../FormStepContainer";

import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { FundraiserContext, ImageUploadContext, ImageUploaderHandle } from "@/components/ImageUploader/types";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete as DeleteIcon } from "@/icons/outlined";

export type StepLogoFormData = {
  logo: string | null;
  isUploading: boolean;
};

export type LiveFormEditorV2StepLogoStatelessProps = {
  form: UseFormReturn<StepLogoFormData>;
  handleSaveAndNext: () => Promise<void>;
  handleSaveAndBack: () => Promise<void>;
  amplitudePayload: Record<string, unknown>;
  logoRef: RefObject<ImageUploaderHandle | null>;
  fundraiserContext?: FundraiserContext; // allows donors to upload images for their P2P campaigns
};

export const LiveFormEditorV2StepLogoStateless: FC<LiveFormEditorV2StepLogoStatelessProps> = ({
  form,
  handleSaveAndNext,
  handleSaveAndBack,
  amplitudePayload,
  logoRef,
  fundraiserContext,
}) => {
  const { t } = useTranslate();
  const logoValue = useWatch({ control: form.control, name: "logo" });
  const isUploading = useWatch({ control: form.control, name: "isUploading" });

  const { isDirty } = form.formState;

  const getNextButtonLabel = () => {
    if (isDirty) {
      return "next";
    }
    return logoValue ? "next" : "skip";
  };

  const deleteLogo = () => {
    form.setValue("logo", null, { shouldValidate: true, shouldDirty: true });
    if (logoRef.current) {
      logoRef.current.setImage(null);
    }
  };

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepLogo.title")}
      description={t("dashboard", "liveFormEditor.stepLogo.description")}
      data-test="logo-step-live-form-editor"
      form={form}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        nextButtonLabel: getNextButtonLabel(),
        disableNext: isUploading,
        disableBack: isUploading,
      }}
    >
      <LiveEditorBlock gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "liveFormEditor.stepLogo.logo")}
          </DashboardTypography>
          <DashboardIconButton size="small" onClick={deleteLogo} vibe="neutral" data-test="delete-logo-button">
            <DeleteIcon />
          </DashboardIconButton>
        </Stack>

        <Box sx={{ width: "100%", maxWidth: 320, marginX: "auto" }}>
          <ImageUploader
            ref={logoRef}
            data-test="logo-image-input"
            field={{ form, name: "logo", isUploadingName: "isUploading" }}
            editor={{ kind: "square", targetSize: 400, centerFit: true, whiteBackground: true }}
            layout={{ ratio: Ratio.Square }}
            upload={{
              context: ImageUploadContext.LiveFormEditorLogo,
              preset: "logo-preset",
              amplitudePayload,
              fundraiserContext,
            }}
            library={{ defaultTab: "myImages" }}
            labels={{ import: t("dashboard", "donationForm.uploadLogo") }}
          />
        </Box>
      </LiveEditorBlock>
    </FormStepContainer>
  );
};
